import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { isNumber } from 'lodash-es';
import { merge, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { CustomTranslationsService } from 'src/app/operational-data-common/translations';

import {
  ActivateDeactivateCommands,
  BufferTemperatureMainProperties,
  ComfortEnsuringCommands,
  ComfortEnsuringProperties,
  CommonFlowTemperature,
  ConsolidatedFeature,
  ConsolidatorDevice,
  DcSensorValueProperties,
  DeviceFirmwareVersion,
  DeviceRemoteResetCommands,
  DeviceRoles,
  DiverterValveProperty,
  EssConfigurationProperties,
  EssInverterPowerProperties,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingBurnersNErrorStatusProperties,
  HeatingBurnersStatisticsProperties,
  HeatingCascadeTemperatureCommonSupplyCommands,
  HeatingCompressorsProperties,
  HeatingConfigurationInternalPumps,
  HeatingConfigurationRegulationProperties,
  HeatingDeviceTimeCommands,
  HeatingDhwProperties,
  HeatingPrimaryCircuitAirSourceProperties,
  IsActiveProperty,
  IsEnabledProperty,
  IsNumberBlockedProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  NumberValue,
  OffPeakSignal,
  OnOff,
  PropertiesValue,
  PvSignalProperties,
  ServiceTimeBasedCommands,
  ServiceTimeBasedProperties,
  SetNumberValueCommands,
  TemperatureSeparatorProperty,
  VentilationFanProperties,
  VentilationFilterRuntime,
  VentilationHeatExchangerFrostprotectionProperties,
  VentilationPreHeaterStatisticsProperties,
  frostProtectionProperties,
  offPeakSignalProperties,
  pvSignal,
} from '@vi-iot/client';

import { ViAlertType, ViConfirmDialog, ViConfirmDialogService } from '@vi-iot/ui';

import { MatTooltip } from '@angular/material/tooltip';
import { concatLatestFrom } from '@ngrx/effects';
import { GTMService } from '@services';
import {
  AuthStore,
  DeviceTypes,
  DevicesStore,
  FeatureService,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RichDevice,
  SmartHomeComponentsStore,
  isReady,
} from '@store';
import moment from 'moment';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { InstallationService } from '../../../../services/installation.service';
import { VentilationDiagnosticsHelpers } from '../../../ventilation/ventilation-overlay/ventilation-diagnostic-information/ventilation-diagnostic-helpers';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { SUFFIX } from 'src/app/shared/common/units';

@Component({
  selector: 'app-device-overlay',
  templateUrl: './device-overlay.component.html',
  styleUrls: ['./device-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceOverlayComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  @Input() device?: RichDevice;
  @Input() title: string;
  @Input() showSystemDetails: boolean;
  @Input() isV2Cascade = false;

  private ngUnsubscribe$: Subject<void> = new Subject();

  readonly heatingDevice = DeviceTypes.Heating;
  readonly alertTypeInfo: ViAlertType = 'info';
  readonly featureCommands = FeatureCommands;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly DeviceTypes = DeviceTypes;
  readonly offPeakSignal = OffPeakSignal;
  readonly DeviceRoles = DeviceRoles;
  readonly Number = Number;
  readonly VentilationDiagnosticsHelpers = VentilationDiagnosticsHelpers;
  readonly suffix = SUFFIX;

  burner$: Observable<Feature<IsActiveProperty, never>>;
  compressor$: Observable<Feature<HeatingCompressorsProperties, never>>;
  // TODO VIG-3076 commented temporarily
  // defrosting$: Observable<Feature<IsActiveProperty>>;
  burnerStatistics$: Observable<Feature<HeatingBurnersStatisticsProperties, never>>;
  modulation$: Observable<Feature<IsNumberValueProperty, never>>;
  boilerTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  commonFlowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  outsideTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  returnFlowTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  waterPressure$: Observable<Feature<DcSensorValueProperties, never>>;
  bufferTankTemperetaure$: Observable<Feature<DcSensorValueProperties, never>>;
  burnerErrorStatus$: Observable<Feature<HeatingBurnersNErrorStatusProperties, never>>;
  isMultiFamily$: Observable<Feature<IsActiveProperty, never>>;
  heatingRegulation$: Observable<Feature<HeatingConfigurationRegulationProperties, never>>;
  secondaryCircuitSupplyTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  essSystemType$: Observable<Feature<IsStringValueProperty, never>>;
  essFirmware$: Observable<Feature<DeviceFirmwareVersion, never>>;
  energyMeter$: Observable<Feature<IsActiveProperty, never>>;
  ambientTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  humidityDewPoint0$: Observable<Feature<DcSensorValueProperties, never>>;
  humidityDewPoint1$: Observable<Feature<DcSensorValueProperties, never>>;
  powerGridConnectionPoint$: Observable<Feature<IsNumberValueProperty, never>>;
  emergencyMode$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  inverterPower$: Observable<Feature<EssInverterPowerProperties, unknown>>;
  inverterPowerConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  inverterPowerConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  inverterPowerConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  inverterPowerConsolidatedDeviceId: string;
  comfortEnsuringMode$: Observable<Feature<ComfortEnsuringProperties, ComfortEnsuringCommands>>;
  burnerDemandTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  calculatedOutsideTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  bufferTemperatureMain$: Observable<Feature<BufferTemperatureMainProperties, never>>;
  leadDeviceCommonSupply$: Observable<
    Feature<CommonFlowTemperature, HeatingCascadeTemperatureCommonSupplyCommands>
  >;
  comfortEnsuringModeUpdatePending$: Observable<boolean>;
  isE3HeatPumpDevice$: Observable<boolean>;
  isVitopureDevice$: Observable<boolean>;
  enabledRefrigerantCircuits$: Observable<Feature<IsEnabledProperty, never>>;
  volumetricFlow$: Observable<Feature<DcSensorValueProperties, never>>;
  OnOff = OnOff;
  dhw$: Observable<Feature<HeatingDhwProperties, never>>;
  heatingCopTotal$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingCopHeating$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingCopDhw$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingCopCooling$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingCopPhotovoltaic$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingSpfTotal: Observable<Feature<IsNumberValueProperty, never>>;
  heatingSpfHeating$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingSpfDhw$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingSeerCooling$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingDeviceTime$: Observable<Feature<never, HeatingDeviceTimeCommands>>;
  heatingBoilerAshLevel$: Observable<Feature<IsNumberValueProperty, never>>;
  ventilationFanExhaust$: Observable<Feature<VentilationFanProperties, never>>;
  ventilationFanExhaustRuntime$: Observable<Feature<IsNumberValueProperty, never>>;
  ventilationFanSupply$: Observable<Feature<VentilationFanProperties, never>>;
  ventilationFanSupplyRuntime$: Observable<Feature<IsNumberValueProperty, never>>;
  ventilationHeatRecovery$: Observable<Feature<IsNumberValueProperty, never>>;
  ventilationVolumeFlowCurrentOutput$: Observable<Feature<VentilationFanProperties, never>>;
  ventilationVolumeFlowCurrentInput$: Observable<Feature<VentilationFanProperties, never>>;
  memberId$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingNoiseReduction$: Observable<string>;
  primaryCircuitAirSource$: Observable<Feature<HeatingPrimaryCircuitAirSourceProperties, never>>;
  spaceFrostProtection$: Observable<Feature<IsActiveProperty, never>>;
  frostProtection$: Observable<Feature<frostProtectionProperties, never>>;
  pvSignal$: Observable<Feature<PvSignalProperties, never>>;
  offPeakSignal$: Observable<Feature<offPeakSignalProperties, never>>;
  lastFilterChange$: Observable<Date>;
  nextFilterChange$: Observable<Date>;
  heatExchangerfrostProtection$: Observable<
    Feature<VentilationHeatExchangerFrostprotectionProperties, never>
  >;
  preHeaterStatistics$: Observable<Feature<VentilationPreHeaterStatisticsProperties, never>>;
  filterRuntime$: Observable<Feature<VentilationFilterRuntime, never>>;
  deviceTimeOffset$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingServiceTimeBased$: Observable<
    Feature<ServiceTimeBasedProperties, ServiceTimeBasedCommands>
  >;
  offSetDeviceTime$: Observable<Date>;
  filterPollution$: Observable<Feature<IsNumberBlockedProperty, never>>;
  deviceRemoteReset$: Observable<Feature<never, DeviceRemoteResetCommands>>;
  essConfigurations$: Observable<Feature<EssConfigurationProperties, never>>;
  heatDhw$: Observable<Feature<DiverterValveProperty, never>>;
  deviceDemandExternal$: Observable<Feature<IsActiveProperty, never>>;
  deviceLockExternal$: Observable<Feature<IsActiveProperty, never>>;
  secondaryHeatGeneratorTemperatureSeparator$: Observable<
    Feature<TemperatureSeparatorProperty, never>
  >;
  heatingBurnersActivation$: Observable<Feature<IsActiveProperty, never>>;
  gasRelatedDataType$: Observable<Feature<IsStringValueProperty, never>>;
  gasRelatedDataCalorificValue$: Observable<Feature<IsNumberValueProperty, SetNumberValueCommands>>;
  gasRelatedDataVolumeCorrectionFactor$: Observable<
    Feature<IsNumberValueProperty, SetNumberValueCommands>
  >;

  deviceDemandExternalFlowTemperatureSetpoint$: Observable<Feature<IsNumberValueProperty, never>>;
  pvAvailable: pvSignal;
  isDeviceVitocharge = false;
  ecotronicStandbyModeToggle$: Observable<boolean>;
  ecotronicToggleControl = new FormControl(false);

  private ecotronicStandbyModeToggleTriggeredByUser$ = new Subject<boolean>();
  internalHydraulicConfigurationInternalPumps$: Observable<
    Feature<HeatingConfigurationInternalPumps, never>
  >;
  configurationDhwCylinderPumps$: Observable<Feature<HeatingConfigurationInternalPumps, never>>;

  constructor(
    public authStore: AuthStore,
    public installationDetailsStore: InstallationDetailsStore,
    public devicesStore: DevicesStore,
    public installationService: InstallationService,
    public smartHomeComponentsStore: SmartHomeComponentsStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    private gtmService: GTMService,
    private featureService: FeatureService,
    protected customTranslationsService: CustomTranslationsService,
    private translate: TranslateService,
    private viConfirmDialogService: ViConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.featureService
      .getConsolidatedFeatures(FeatureNames.inverterPowerConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.inverterPowerConsolidatedDeviceId = feature?.deviceId;
      });
    this.pvAvailable = pvSignal.pvAvailable;
    this.offPeakSignal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceOffPeakSignal,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.frostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.frostProtection,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingServiceTimeBased$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.serviceTimeBased,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.spaceFrostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.spaceFrostProtection,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.pvSignal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.devicePvSignal,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.devicesStore.selectedDevice$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedDevice) => {
        this.device = this.device ? this.device : selectedDevice;
        this.isDeviceVitocharge = this.device.roles.includes(DeviceRoles.ProductVitocharge);

        this.initObservables();
      });

    this.offSetDeviceTime$ = this.installationFeaturesStore.getOffsetDeviceTime();

    this.memberId$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      PropertiesValue<NumberValue>,
      never
    >(FeatureNames.cascadeMemberId, this.device?.id);

    this.heatExchangerfrostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationHeatExchangerFrostprotection,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.preHeaterStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.preHeaterStatistics,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.filterRuntime$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.filterRuntime,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.deviceTimeOffset$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceTimeOffset,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.heatingNoiseReduction$ = this.installationFeaturesStore.getHeatingNoiseReductionMode(
      this.device.id,
      this.device.gatewaySerial
    );

    this.isVitopureDevice$ = this.installationDetailsStore.hasSelectedDeviceGivenRoles([
      DeviceRoles.ProductVitopure,
    ]);

    const localDate$ = this.deviceTimeOffset$.pipe(
      map((feature) =>
        moment()
          .utcOffset(0)
          .add(feature?.properties?.value?.value, this.featurePropertyKey.minutes)
      )
    );

    this.lastFilterChange$ = this.filterRuntime$.pipe(
      concatLatestFrom(() => localDate$),
      map(([filterRuntime, localDate]) =>
        localDate
          .subtract(filterRuntime?.properties?.operatingHours?.value, this.featurePropertyKey.hours)
          .toDate()
      )
    );

    this.nextFilterChange$ = this.filterRuntime$.pipe(
      concatLatestFrom(() => localDate$),
      map(([filterRuntime, localDate]) =>
        localDate
          .add(filterRuntime?.properties?.remainingHours?.value, this.featurePropertyKey.hours)
          .toDate()
      )
    );
  }

  calculateClocking(amountOfStarts: number, hoursRun: number): number {
    if (amountOfStarts) {
      return (hoursRun / amountOfStarts) * 60;
    }

    return NaN;
  }

  isClockingDataAvailable(amountOfStarts: number, hoursRun: number): boolean {
    return isNumber(amountOfStarts) && isNumber(hoursRun);
  }

  onComfortEnsuringModeDeactivate(event: MouseEvent, checked: boolean): void {
    if (checked) {
      this.changeEnsuringModeFeature(checked);

      this.gtmService.track('onConfirm', 'Deactivating comfort ensuring mode', 'systemDetails');
    } else {
      event.preventDefault();
    }
  }

  cancelActivationComfortEnsuringMode(): void {
    this.gtmService.track('onCancel', 'Activating comfort ensuring mode', 'systemDetails');
  }

  onComfortEnsuringModeActivate(comfortEnsuringToggle: MatSlideToggle, checked: boolean): void {
    this.changeEnsuringModeFeature(checked);
    this.onComfortEnsuringSwitchChange(comfortEnsuringToggle);
    this.gtmService.track('onConfirm', 'Activating comfort ensuring mode', 'systemDetails');
  }

  onComfortEnsuringSwitchChange(comfortEnsuringToggle: MatSlideToggle): void {
    comfortEnsuringToggle.toggle();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initObservables(): void {
    this.burner$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerStatus,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.compressor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressorStatus,
      this.device?.id,
      this.device?.gatewaySerial
    );
    // TODO VIG-3076 commented temporarily
    // this.defrosting$ = this.selectDeviceFeature(FeatureNames.outdoorDefrosting);

    this.burnerStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerStatistics,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.modulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerModulation,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.boilerTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.boilerTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.commonFlowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.boilerCommonSupplyTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.outsideTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.outsideTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.returnFlowTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.returnFlowTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.waterPressure$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.waterPressure,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.bufferTankTemperetaure$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.hydraulicSeparatorTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.isMultiFamily$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationMultiFamilyHouse,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingRegulation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationRegulation,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.burnerErrorStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerErrorStatus,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.secondaryCircuitSupplyTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.secondaryCircuitSupplyTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.essSystemType$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essSystemType,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.essFirmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.energyMeter$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essEnergyMeter(),
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.ambientTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essAmbientTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );
    // Humidity dew point can be on heating.circuit 0 or 1 only.
    this.humidityDewPoint0$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSensorsHumidityDewpoint('0'),
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.humidityDewPoint1$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuitSensorsHumidityDewpoint('1'),
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.powerGridConnectionPoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essGridTransferPowerExchange,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.emergencyMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceEmergencyOperation,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.inverterPower$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.inverterPower,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.inverterPowerConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.inverterPowerConsolidated,
      this.device?.id
    );

    this.inverterPowerConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.inverterPowerConsolidated
    );

    this.inverterPowerConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.inverterPowerConsolidated,
      this.device?.id,
      this.inverterPowerConsolidatedDeviceId
    );
    this.comfortEnsuringMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceComfortEnsuring,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.comfortEnsuringModeUpdatePending$ = this.devicesStore.isFeatureWithPendingUpdates(
      FeatureNames.heatingDeviceComfortEnsuring
    );
    this.burnerDemandTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerDemandTemperature,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.calculatedOutsideTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.calculatedTemperatureOutside,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.bufferTemperatureMain$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.bufferCylinderTemperatureMain,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.leadDeviceCommonSupply$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.cascadeHeatingCommonSupply,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.isE3HeatPumpDevice$ = this.devicesStore.isE3HeatpumpDevice(this.device);
    this.enabledRefrigerantCircuits$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.compressors,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.volumetricFlow$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.volumetricFlowReturn,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingBoilerAshLevel$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingBoilerAshLevel,
      this.device?.id,
      this.device?.gatewaySerial
    );

    // SPF DHW
    this.dhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhw,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingSpfTotal = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingSpfTotal,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingCopTotal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingCopTotal,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingCopHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingCopHeating,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingCopDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingCopDhw,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingCopCooling$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingCopCooling,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingCopPhotovoltaic$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingCopPhotovoltaic,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingSpfHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingSpfHeating,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingSpfDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingSpfDhw,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingDeviceTime$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceTime,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.heatingSeerCooling$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingSeerCooling,
      this.device?.id,
      this.device?.gatewaySerial
    );

    // Ventilation
    this.ventilationFanExhaust$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFanExhaust,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.ventilationFanExhaustRuntime$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFanExhaustRuntime,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.ventilationFanSupply$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFanSupply,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.ventilationFanSupplyRuntime$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFanSupplyRuntime,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.ventilationHeatRecovery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationHeatRecovery,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.filterPollution$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationFilterPollutionBlocked,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.ventilationVolumeFlowCurrentOutput$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationVolumeFlowCurrentOutput,
      this.device?.id,
      this.device?.gatewaySerial
    );
    this.ventilationVolumeFlowCurrentInput$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationVolumeFlowCurrentInput,
      this.device?.id,
      this.device?.gatewaySerial
    );

    // Primary circuit
    this.primaryCircuitAirSource$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.primaryCircuitAirSource,
      this.device?.id,
      this.device?.gatewaySerial
    );

    this.deviceRemoteReset$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceRemoteReset,
      this.device?.id,
      this.device?.gatewaySerial
    );

    // Configuration
    this.essConfigurations$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFeatures,
      this.device?.id,
      this.device?.gatewaySerial
    );
    //heatDhw
    this.heatDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatDhwDiverter,
      this.device?.id,
      this.device?.gatewaySerial
    );

    // device.demand.external
    this.deviceDemandExternal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceDemandExternal,
      this.device?.id,
      this.device?.gatewaySerial
    );

    // device.lock.external
    this.deviceLockExternal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceLockExternal,
      this.device?.id,
      this.device?.gatewaySerial
    );

    //pump.limit.values
    this.internalHydraulicConfigurationInternalPumps$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.boilerPumpsInternalTarget,
      this.device?.id
    );

    this.configurationDhwCylinderPumps$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationdhwCylinderPumps,
      this.device?.id
    );

    this.secondaryHeatGeneratorTemperatureSeparator$ = this.installationFeaturesStore.selectInstallationGatewayFeatureByDeviceId(
      FeatureNames.secondaryHeatGeneratorTemperatureSeparator,
      this.device.id,
      this.device.gatewaySerial
    );
    this.deviceDemandExternalFlowTemperatureSetpoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceDemandExternalFlowTemperatureSetpoint,
      this.device.id,
      this.device.gatewaySerial
    );
    this.heatingBurnersActivation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerManualActivation,
      this.device.id,
      this.device.gatewaySerial
    );

    this.gasRelatedDataType$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConfigurationType,
      this.device.id,
      this.device.gatewaySerial
    );

    this.gasRelatedDataCalorificValue$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConfigurationCalorificValue,
      this.device.id,
      this.device.gatewaySerial
    );

    this.gasRelatedDataVolumeCorrectionFactor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConfigurationCorrectionFactor,
      this.device.id,
      this.device.gatewaySerial
    );

    this.initToggleEcotronicLogic();
  }

  private initToggleEcotronicLogic(): void {
    this.ecotronicStandbyModeToggle$ = merge(
      this.ecotronicStandbyModeToggleTriggeredByUser$,
      this.heatingBurnersActivation$.pipe(
        filter((feature) => isReady(feature)),
        map((feature) => !feature.properties.active.value)
      )
    ).pipe(
      tap((value) => {
        this.ecotronicToggleControl.setValue(value, { emitEvent: false });
      })
    );
  }

  private changeEnsuringModeFeature(checked: boolean): void {
    if (this.isV2Cascade) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.heatingDeviceComfortEnsuring,
        !checked,
        this.featurePropertyKey.activated,
        '',
        !checked ? FeatureCommands.activateManually : FeatureCommands.deactivateManually,
        null,
        this.device?.id
      );
    } else {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.heatingDeviceComfortEnsuring,
        !checked,
        this.featurePropertyKey.activated,
        '',
        !checked ? FeatureCommands.activateManually : FeatureCommands.deactivateManually
      );
    }
  }

  private onBurnerManualActivationChange(isActive: boolean): void {
    if (!isActive) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.burnerManualActivation,
        true,
        this.featurePropertyKey.active,
        '',
        FeatureCommands.activate
      );

      this.gtmService.track('onToggle', 'Burner manual activation', 'Ecotronic');
    } else {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.burnerManualActivation,
        false,
        this.featurePropertyKey.active,
        '',
        FeatureCommands.deactivate
      );
      this.gtmService.track('onToggle', 'Burner manual deactivation', 'Ecotronic');
    }
  }

  private cancelStanbyModeToggle(): void {
    this.gtmService.track('onCancel', 'Toggle Ecotronic Standby Mode', 'systemDetails');
  }

  onEcotronicToggleChange(
    feature: Feature<IsActiveProperty, never>,
    originalToggleValue: boolean
  ): void {
    this.ecotronicStandbyModeToggleTriggeredByUser$.next(originalToggleValue);

    const title = feature.properties.active.value
      ? this.translate.instant('+INSTALLATIONS.INSTALLATION.ECOTRONIC.DEACTIVATION.MODAL.TITLE')
      : this.translate.instant('+INSTALLATIONS.INSTALLATION.ECOTRONIC.ACTIVATION.MODAL.TITLE');
    const content = feature.properties.active.value
      ? this.translate.instant('+INSTALLATIONS.INSTALLATION.ECOTRONIC.DEACTIVATION.MODAL.CONTENT')
      : this.translate.instant('+INSTALLATIONS.INSTALLATION.ECOTRONIC.ACTIVATION.MODAL.CONTENT');

    const modalData = {
      title,
      content,
      cancelBtn: this.translate.instant('GENERAL.BUTTON.CANCEL'),
      confirmBtn: this.translate.instant('GENERAL.BUTTON.ACCEPT'),
    } as ViConfirmDialog;

    this.viConfirmDialogService.open(modalData).subscribe((res) => {
      this.ecotronicStandbyModeToggleTriggeredByUser$.next(res);
      if (res) {
        this.onBurnerManualActivationChange(!originalToggleValue);
      } else {
        this.cancelStanbyModeToggle();
        this.ecotronicStandbyModeToggleTriggeredByUser$.next(originalToggleValue);
      }
    });
  }

  rebootDevice(): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.deviceRemoteReset,
      null,
      null,
      null,
      FeatureCommands.remoteReset,
      this.device?.gatewaySerial,
      this.device?.id
    );
    this.gtmService.track('rebootDevice');
  }

  cancelRebootDeviceConfirmation(): void {
    this.gtmService.track('onCancel', 'Reboot device confirmation', 'system');
  }
}
